import { Vector3 } from "three";
import { EvaluationResult } from "../../../evaluators/EvaluationResult";
import { ParameterDataType } from "../../../evaluators/ParameterDataType";
import { DataTypes } from "./DataTypes";
import { LomaFunction } from "../LomaFunction";
import { LomaVariable } from "../LomaVariable";

export class Vectors
{
  static functions:LomaFunction[] =
  [
    LomaFunction.create( 
      "length",
       ( e, fe, values )=>
       { 
        let v = values[ 0 ].data as Vector3;

        if ( values.length != 1 )
        {
          return EvaluationResult.asError( fe, "length() > Invalid number of parameters: " + values.length )
        }

        return EvaluationResult.withValue( v.length(), DataTypes.Number );
       }, 
       [ 
        ParameterDataType.from( "vector", DataTypes.Vector3 )
       ],  
       DataTypes.Number 
    ).setInfo(
      {
        en: "Computes the Euclidean length (straight-line length) from (0, 0, 0) to (x, y, z)"
      }
    ),

    LomaFunction.create( 
      "distance",
       ( e, fe, values )=>
       { 
        let a = values[ 0 ].data as Vector3;
        let b = values[ 1 ].data as Vector3;

        return EvaluationResult.withValue( b.clone().sub( a ).length(), DataTypes.Number );
       }, 
       [ 
        ParameterDataType.from( "a", DataTypes.Vector3 ), 
        ParameterDataType.from( "b", DataTypes.Vector3 )
       ],  
       DataTypes.Number 
    ).setInfo(
      {
        en: "Computes the Euclidean length (straight-line length) from a to b"
      }
    ),

    LomaFunction.create( 
      "lerp",
       ( e, fe, values )=>
       { 
        let a = values[ 0 ].data as Vector3;
        let b = values[ 1 ].data as Vector3;
        let t = values[ 2 ].data as number;

        let lerped = new Vector3();

        return EvaluationResult.withValue( lerped.lerpVectors( a, b, t ), DataTypes.Vector3 );
       }, 
       [ 
        ParameterDataType.from( "a", DataTypes.Vector3 ), 
        ParameterDataType.from( "b", DataTypes.Vector3 ), 
        ParameterDataType.from( "t", DataTypes.Number )
       ],  
       DataTypes.Vector3 
    ).setInfo(
      {
        en: "Linearly interpolate between the vectors a and b with t from [0 to 1] as lerping amount where 0 is a, and 1 is b"
      }
    ),
    

    LomaFunction.create( 
      "normalize",
       ( e, fe, values )=>
       { 
        let v = values[ 0 ].data as Vector3;

        return EvaluationResult.withValue( v.clone().normalize(), DataTypes.Vector3 );
       }, 
       [ 
        ParameterDataType.from( "vector", DataTypes.Vector3 )
       ],  
       DataTypes.Vector3 
    ).setInfo(
      {
        en: "Normalizes the vector (its length will be 1)"
      }
    ),    

    LomaFunction.create( 
      "reflect",
       ( e, fe, values )=>
       { 
        let v = values[ 0 ].data as Vector3;
        let n = values[ 1 ].data as Vector3;
        n = n.clone().normalize();

        return EvaluationResult.withValue( v.clone().reflect( n ), DataTypes.Vector3 );
       }, 
       [ 
        ParameterDataType.from( "vector", DataTypes.Vector3 ), 
        ParameterDataType.from( "reflectionNormal", DataTypes.Vector3 )
       ],  
       DataTypes.Vector3 
    ).setInfo(
      {
        en: "Reflects the vector by the reflection normal"
      }
    ),

    LomaFunction.create( 
      "projectOnPlane",
       ( e, fe, values )=>
       { 
        let v = values[ 0 ].data as Vector3;
        let n = values[ 1 ].data as Vector3;
        n = n.clone().normalize();

        return EvaluationResult.withValue( v.clone().projectOnPlane( n ), DataTypes.Vector3 );
       }, 
       [ 
        ParameterDataType.from( "vector", DataTypes.Vector3 ), 
        ParameterDataType.from( "planeNormal", DataTypes.Vector3 ) 
       ],  
       DataTypes.Vector3 
    ).setInfo(
      {
        en: "Projects this vector onto a plane by subtracting this vector projected onto the plane's normal from this vector"
      }
    ),
    LomaFunction.create( 
      "projectOnVector",
       ( e, fe, values )=>
       { 
        let a = values[ 0 ].data as Vector3;
        let b = values[ 1 ].data as Vector3;

        return EvaluationResult.withValue( a.clone().projectOnVector( b ), DataTypes.Vector3 );
       }, 
       [ 
        ParameterDataType.from( "a", DataTypes.Vector3 ), 
        ParameterDataType.from( "b", DataTypes.Vector3 )
       ],  
       DataTypes.Vector3 
    ).setInfo(
      {
        en: "Projects this vector onto v"
      }
    ),


    LomaFunction.create( 
      "dot",
       ( e, fe, values )=>
       { 
        let a = values[ 0 ].data as Vector3;
        let b = values[ 1 ].data as Vector3;

        return EvaluationResult.withValue( a.clone().dot( b ), DataTypes.Number );
       }, 
       [ 
        ParameterDataType.from( "a", DataTypes.Vector3 ), 
        ParameterDataType.from( "b", DataTypes.Vector3 ) 
       ],  
       DataTypes.Number 
    ).setInfo(
      {
        en: "Calculate the dot product of a and b"
      }
    ),


    LomaFunction.create( 
      "cross",
       ( e, fe, values )=>
       { 
        let a = values[ 0 ].data as Vector3;
        let b = values[ 1 ].data as Vector3;

        return EvaluationResult.withValue( a.clone().cross( b ), DataTypes.Vector3 );
       }, 
       [ 
        ParameterDataType.from( "a", DataTypes.Vector3 ), 
        ParameterDataType.from( "b", DataTypes.Vector3 )
       ],  
       DataTypes.Vector3 
    ).setInfo(
      {
        en: "Calculate the cross product of a and b"
      }
    ),


    LomaFunction.create( 
      "angleTo",
       ( e, fe, values )=>
       { 
        let a = values[ 0 ].data as Vector3;
        let b = values[ 1 ].data as Vector3;

        let angle = a.clone().angleTo( b );

        return EvaluationResult.withValue( angle, DataTypes.Number );
       }, 
       [ 
        ParameterDataType.from( "a", DataTypes.Vector3 ), 
        ParameterDataType.from( "b", DataTypes.Vector3 )
       ],  
       DataTypes.Number 
    ).setInfo(
      {
        en: "Returns the angle between a and b in radians"
      }
    )
  ]   
  
}